// src/components/ProtectedRoute.js
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { matchPageKey } from '../config/pageAccess';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { currentUser, userRole, isSupervisor, isFired, pageOverrides, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Verifying Access...</div>;
  }

  // ✅ 1) Not logged in
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // ✅ 2) Fired / Terminated users cannot access anything
  // (حتى لو كانوا مسجلين دخول)
  if (isFired) {
    return (
      <Navigate
        to="/account-disabled"
        state={{
          from: location,
          error: 'Your account has been disabled. Please contact your manager.',
        }}
        replace
      />
    );
  }

  // ✅ 3) Per-employee override (set by a Manager on the Team Access page). An explicit override
  // for this page wins over the role default in both directions: `false` blocks even a role that
  // would normally be allowed, `true` grants even a role that would normally be denied.
  const pageKey = matchPageKey(location.pathname);
  const override = pageKey ? pageOverrides?.[pageKey] : undefined;

  if (override === false) {
    return (
      <Navigate
        to="/dashboard"
        state={{ error: 'You do not have permission to access this page.' }}
        replace
      />
    );
  }
  if (override === true) {
    return children;
  }

  // ✅ 4) If no roles specified → any active authenticated user can access
  if (!allowedRoles || allowedRoles.length === 0) {
    return children;
  }

  // ✅ 5) Roles + Supervisor virtual role
  const effectiveRoles = [userRole].filter(Boolean);
  if (isSupervisor) effectiveRoles.push('Supervisor');

  const hasAccess = effectiveRoles.some((role) => allowedRoles.includes(role));

  if (hasAccess) return children;

  console.warn(
    `Access Denied for route: ${location.pathname}.
User's effective roles: [${effectiveRoles.join(', ')}].
Route requires one of: [${allowedRoles.join(', ')}]`
  );

  return (
    <Navigate
      to="/dashboard"
      state={{ error: 'You do not have permission to access this page.' }}
      replace
    />
  );
};

export default ProtectedRoute;
